import { useState, type FormEvent } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLocation, useNavigate } from "react-router";
import { ApiClientError } from "@victor/api-client";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Logo } from "../../ui/Logo";
import { api } from "../../lib/api";

type Stage = "credentials" | "mfa" | "forgot" | "forgot-sent";

/**
 * Branding mémorisé à la dernière connexion sur ce poste (AppShell l'écrit
 * en localStorage). Pas de white-label : avant la première connexion,
 * l'écran reste Victor.
 */
function loadLoginBranding(): { logoUrl: string; nom: string } | null {
  try {
    const raw = localStorage.getItem("victor.login.branding");
    if (!raw) return null;
    const b = JSON.parse(raw) as { logoUrl?: string; nom?: string };
    return b.logoUrl ? { logoUrl: b.logoUrl, nom: b.nom ?? "" } : null;
  } catch {
    return null;
  }
}
type LocationState = { from?: string } | null;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeMfa } = useAuth();

  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [brand] = useState(loadLoginBranding);

  const redirectAfterLogin = (() => {
    const state = location.state as LocationState;
    return state?.from && state.from.startsWith("/") ? state.from : "/";
  })();

  async function handleCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await login({ email, password });
      if ("mfaRequired" in res && res.mfaRequired) {
        setMfaToken(res.mfaToken);
        setStage("mfa");
      } else {
        navigate(redirectAfterLogin, { replace: true });
      }
    } catch (err) {
      setError(formatError(err, "Email ou mot de passe incorrect."));
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await completeMfa(mfaToken, mfaCode);
      navigate(redirectAfterLogin, { replace: true });
    } catch (err) {
      setError(formatError(err, "Code MFA invalide."));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.auth.forgot({ email: forgotEmail });
      setStage("forgot-sent");
    } catch {
      // On affiche succès quoi qu'il arrive (anti-énumération)
      setStage("forgot-sent");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white p-4">
      <AmbientBackground />

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="rounded-2xl bg-white p-8 shadow-2xl shadow-navy-800/10 ring-1 ring-navy-100 sm:p-10">
          <div className="flex flex-col items-center gap-3 text-center">
            {brand ? (
              <>
                <img
                  src={brand.logoUrl}
                  alt={brand.nom || "Logo"}
                  className="logo-agence h-14 w-auto max-w-[220px] object-contain"
                />
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-400">
                  Propulsé par <Logo size={14} withWordmark />
                </span>
              </>
            ) : (
              <Logo size={48} withWordmark />
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="mt-8"
            >
              {stage === "credentials" ? (
                <CredentialsForm
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  onSubmit={handleCredentialsSubmit}
                  onForgot={() => {
                    setForgotEmail(email);
                    setError(null);
                    setStage("forgot");
                  }}
                  error={error}
                  loading={loading}
                />
              ) : null}

              {stage === "mfa" ? (
                <MfaForm
                  code={mfaCode}
                  setCode={setMfaCode}
                  onSubmit={handleMfaSubmit}
                  onCancel={() => {
                    setStage("credentials");
                    setMfaCode("");
                    setMfaToken("");
                    setError(null);
                  }}
                  error={error}
                  loading={loading}
                />
              ) : null}

              {stage === "forgot" ? (
                <ForgotForm
                  email={forgotEmail}
                  setEmail={setForgotEmail}
                  onSubmit={handleForgotSubmit}
                  onCancel={() => {
                    setStage("credentials");
                    setError(null);
                  }}
                  loading={loading}
                />
              ) : null}

              {stage === "forgot-sent" ? (
                <ForgotSent
                  email={forgotEmail}
                  onBack={() => {
                    setStage("credentials");
                    setError(null);
                  }}
                />
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>

        <p className="mt-6 text-center text-xs text-navy-500">
          Premier accès ? Votre administrateur vous a envoyé un lien d'invitation.
        </p>
      </motion.div>
    </div>
  );
}

function CredentialsForm(props: {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onForgot: () => void;
  error: string | null;
  loading: boolean;
}) {
  return (
    <form onSubmit={props.onSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy-800">
          Connexion
        </h1>
        <p className="mt-1 text-sm text-navy-500">Heureux de vous revoir.</p>
      </div>

      <Input
        label="Email"
        type="email"
        name="email"
        value={props.email}
        onChange={(e) => props.setEmail(e.target.value)}
        autoComplete="email"
        required
        autoFocus
      />

      <Input
        label="Mot de passe"
        type="password"
        name="password"
        value={props.password}
        onChange={(e) => props.setPassword(e.target.value)}
        autoComplete="current-password"
        required
      />

      {props.error ? <ErrorBanner message={props.error} /> : null}

      <Button type="submit" disabled={props.loading} fullWidth size="lg">
        {props.loading ? "Connexion…" : "Se connecter"}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={props.onForgot}
          className="text-sm font-semibold text-accent-500 hover:text-accent-600"
        >
          Mot de passe oublié ?
        </button>
      </div>
    </form>
  );
}

function MfaForm(props: {
  code: string;
  setCode: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  error: string | null;
  loading: boolean;
}) {
  return (
    <form onSubmit={props.onSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy-800">
          Vérification en deux étapes
        </h1>
        <p className="mt-1 text-sm text-navy-500">
          Entrez le code à 6 chiffres de votre application d'authentification.
          Vous pouvez aussi utiliser un code de récupération.
        </p>
      </div>

      <Input
        label="Code"
        name="mfa-code"
        value={props.code}
        onChange={(e) => props.setCode(e.target.value.replace(/\s/g, ""))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        required
        autoFocus
      />

      {props.error ? <ErrorBanner message={props.error} /> : null}

      <Button type="submit" disabled={props.loading} fullWidth size="lg">
        {props.loading ? "Vérification…" : "Valider"}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={props.onCancel}
          className="text-sm font-semibold text-navy-600 hover:text-navy-800"
        >
          ← Revenir à la connexion
        </button>
      </div>
    </form>
  );
}

function ForgotForm(props: {
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <form onSubmit={props.onSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy-800">
          Mot de passe oublié
        </h1>
        <p className="mt-1 text-sm text-navy-500">
          On vous envoie un lien sécurisé pour le réinitialiser.
        </p>
      </div>

      <Input
        label="Votre email"
        type="email"
        name="forgot-email"
        value={props.email}
        onChange={(e) => props.setEmail(e.target.value)}
        autoComplete="email"
        required
        autoFocus
      />

      <Button type="submit" disabled={props.loading} fullWidth size="lg">
        {props.loading ? "Envoi…" : "Envoyer le lien"}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={props.onCancel}
          className="text-sm font-semibold text-navy-600 hover:text-navy-800"
        >
          ← Revenir à la connexion
        </button>
      </div>
    </form>
  );
}

function ForgotSent({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-navy-800">
        Email envoyé
      </h1>
      <p className="text-sm text-navy-600">
        Si <strong>{email}</strong> correspond à un compte Victor, vous
        recevrez un lien dans les prochaines minutes. Vérifiez vos spams au cas
        où.
      </p>
      <Button onClick={onBack} variant="secondary" fullWidth>
        Revenir à la connexion
      </Button>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200"
    >
      {message}
    </div>
  );
}

function AmbientBackground() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-accent-200/40 blur-3xl" />
      <div className="absolute top-40 left-[-10%] h-[420px] w-[420px] rounded-full bg-navy-100/40 blur-3xl" />
    </div>
  );
}

function formatError(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
